import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import {
  IonPage, IonContent, IonIcon, IonBadge, IonToggle, IonRippleEffect, IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonCard, IonCardHeader, IonCardTitle, IonCardSubtitle, IonGrid, IonRow } from "@ionic/react";
import {
  globeOutline, moonOutline, helpCircleOutline, notificationsOutline,
  playCircleOutline, sendOutline, bugOutline, logoYoutube,
  chevronForwardOutline, documentOutline, documentLockOutline,
  chevronBackOutline, closeOutline, arrowUpCircleOutline,
} from "ionicons/icons";
import { SearchOverlay, SearchButton } from "@/components/TopbarSearch";
import { t, setLanguage as setAppLanguage, useLanguage } from "@/utils/i18n";
import LanguagePicker from "@/components/LanguagePicker";
import { installedAppVersion, compareVersions, latestAppVersion } from "@/utils/appVersion";
import { syncStatusBar } from "@/utils/statusBar";
import { appThemeDark } from "@/utils/appTheme";
import { devicePlatform } from "@/components/AppLayout";
import { youtubeThumb, categoryThumb, youtubeEmbed } from "@/utils/youtube";
import { NoResults } from "@/components/NotificationsDrawer";
import FreshOverlay from "@/components/FreshOverlay";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { isNative } from "@/utils/nativeHttp";
import { pushPref, setPushEnabled } from "@/utils/nativePush";
import { toast } from "@/utils/toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const cardStyle = {
  backgroundColor: "var(--ion-card-background)",
  borderRadius: 12,
  overflow: "hidden",
  boxShadow: "rgba(0,0,0,0.18) 0 4px 24px",
};

const cardTitle = {
  fontSize: "1rem", fontWeight: 700,
  color: "var(--ion-text-color)",
  padding: "14px 20px 12px",
};

function Chevron() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: 18, color: "var(--ion-color-medium)" }}>
      <IonIcon icon={chevronForwardOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
    </span>
  );
}

// the row's leading icon, at the sidebar nav's icon size (24px)
function IconWrap({ icon }) {
  return (
    <div style={{ width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginBottom: 14 }}>
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: 24, color: "var(--ion-color-medium)" }}>
        <IonIcon icon={icon} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
      </span>
    </div>
  );
}

function Row({ icon, label, right, below, last, clickable, onClick }) {
  return (
    <div
      onClick={onClick}
      className={clickable ? "ion-activatable" : undefined}
      style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0 0 20px", cursor: clickable ? "pointer" : "default", position: clickable ? "relative" : undefined, overflow: clickable ? "hidden" : undefined }}
    >
      {clickable && <IonRippleEffect />}
      {icon && <IconWrap icon={icon} />}
      <div style={{
        flex: "1 1 0%", display: "flex", flexDirection: "column",
        paddingBottom: 14, paddingRight: 20,
        borderBottom: last ? "none" : "1px solid var(--ion-border-color)",
        minWidth: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ flex: "1 1 0%", fontSize: "0.9rem", fontWeight: 500, color: "var(--ion-text-color)" }}>{label}</span>
          {right}
        </div>
        {below && <div style={{ marginTop: 10 }}>{below}</div>}
      </div>
    </div>
  );
}

const getYouTubeThumb = youtubeThumb;

function VideoListItem({ video, onClick }) {
  const thumb = getYouTubeThumb(video.youtubeUrl);
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", background: "none", border: "none", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 16px", textAlign: "left",
        borderBottom: "1px solid var(--ion-border-color)",
      }}
    >
      {thumb ? (
        <img src={thumb} alt={video.title} style={{ width: 80, height: 52, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: 80, height: 52, borderRadius: 6, background: "var(--ion-color-step-100)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <IonIcon icon={playCircleOutline} style={{ fontSize: 24, color: "var(--ion-color-medium)" }} />
        </div>
      )}
      <div style={{ flex: "1 1 0%", minWidth: 0 }}>
        <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--ion-text-color)", lineHeight: 1.3 }}>{video.title}</div>
        {video.description && (
          <div style={{
            fontSize: "0.78rem", color: "var(--ion-color-medium)", marginTop: 3, lineHeight: 1.4,
            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>{video.description}</div>
        )}
      </div>
      <IonIcon icon={playCircleOutline} style={{ fontSize: 22, color: "var(--ion-color-primary)", flexShrink: 0 }} />
    </button>
  );
}

export default function AppSettings() {
  const navigate = useNavigate();
  useLanguage(); // re-render when the language changes

  const [settings, setSettings] = useState({
    version: "1.0.0", status: "normal", videoUrl: "", whatsNew: [], knownIssues: ["None :)"],
  });

  const [darkMode,  setDarkMode]  = useState(() => appThemeDark());
  const [language,  setLanguage]  = useState(() => localStorage.getItem("appLanguage") || "en");
  const [showChat,  setShowChat]  = useState(() => localStorage.getItem("appShowSupportChat") !== "false");
  const [allowPush, setAllowPush] = useState(() => pushPref() !== false); // seeded by the install-time permission answer

  const [tutorialOpen,       setTutorialOpen]       = useState(false);
  const [tutorialSearch,     setTutorialSearch]     = useState("");
  const [tutorialSearchOpen, setTutorialSearchOpen] = useState(false);
  const tutorialSearchRef = useRef(null);
  const [tutorialCategories, setTutorialCategories] = useState([]);
  const [selectedCategory,   setSelectedCategory]   = useState(null);
  const [tutorialVideo,      setTutorialVideo]      = useState(null);

  // The version THIS copy runs (native binary via @capacitor/app; null on
  // the web, which is always current). Compared against the admin's latest.
  const [installedV, setInstalledV] = useState(null);
  useEffect(() => { installedAppVersion().then(setInstalledV); }, []);
  const latestV = latestAppVersion(settings, devicePlatform());
  const currentV = installedV || latestV;
  const updateBehind = !!installedV && compareVersions(installedV, latestV) < 0;
  const storeUrl = (settings.loveLinks || {})[devicePlatform()] || "";

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/app-settings`)
      .then(r => r.json())
      .then(d => { if (d.success && d.settings) setSettings(d.settings); })
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/tutorial-categories`)
      .then(r => r.json())
      .then(d => { if (d.success) setTutorialCategories(d.categories || []); })
      .catch(() => {});
  }, []);

  const handleDark = () => {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem("appDarkMode", String(next));
    document.body.classList.toggle("dark", next);
    syncStatusBar(); // phone status bar icons follow the theme
  };

  const handleLanguage = (val) => {
    setLanguage(val);
    setAppLanguage(val); // persists + notifies every subscribed component
  };

  // Shows/hides the floating support chat bubble everywhere in the app
  const handleChatToggle = () => {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    const next = !showChat;
    setShowChat(next);
    localStorage.setItem("appShowSupportChat", String(next));
    window.dispatchEvent(new CustomEvent("mintslip-support-visibility", { detail: { visible: next } }));
  };

  // Native only: the person's own push switch. Off tears the device token
  // down; on re-registers, unless the OS-level permission is denied.
  const handlePushToggle = async () => {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
    const next = !allowPush;
    setAllowPush(next);
    const r = await setPushEnabled(next);
    if (next && r?.blocked) {
      setAllowPush(false);
      toast.error(t("Notifications are blocked for MintSlip in your phone's Settings. Allow them there first."));
    }
  };

  // ?tutorial=open opens the Tutorials modal; ?tutorial=<video id> jumps to
  // that video (both come from the topbar search)
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("tutorial");
    if (!id) return;
    if (id === "open") {
      setTutorialOpen(true);
    } else {
      if (!tutorialCategories.length) return;
      for (const c of tutorialCategories) {
        const v = (c.videos || []).find((x) => x.id === id);
        if (v) { setSelectedCategory(c); setTutorialVideo(v); setTutorialOpen(true); break; }
      }
    }
    window.history.replaceState({}, "", "/app/settings");
  }, [tutorialCategories]); // eslint-disable-line

  const closeTutorialModal = () => {
    setTutorialOpen(false);
    setTutorialSearch("");
    setTutorialSearchOpen(false);
    setSelectedCategory(null);
    setTutorialVideo(null);
  };

  // The modal's search works like the topbar search: an icon until tapped,
  // then the box scales in over the title and gets focus; cancel/Escape
  // collapses it and clears the query.
  const closeTutorialSearch = () => { setTutorialSearchOpen(false); setTutorialSearch(""); };
  useEffect(() => {
    if (!tutorialSearchOpen) return undefined;
    const timer = setTimeout(() => tutorialSearchRef.current?.setFocus?.(), 80);
    return () => clearTimeout(timer);
  }, [tutorialSearchOpen]);

  const handleTutorialBack = () => {
    if (tutorialVideo) { setTutorialVideo(null); }
    else if (selectedCategory) { setSelectedCategory(null); setTutorialSearch(""); }
  };

  // The X waits for its Ionic ripple to show before dismissing: the modal's
  // fade-out starts on the same click and would otherwise swallow the ripple
  // (the same hold the sign-in buttons use).
  const RIPPLE_HOLD_MS = 220;
  const closeTutorialModalAfterRipple = () => { setTimeout(closeTutorialModal, RIPPLE_HOLD_MS); };

  const statusColor = { normal: "success", degraded: "warning", down: "danger" }[settings.status] || "success";
  const statusLabel = t({ normal: "Normal", degraded: "Degraded", down: "Down" }[settings.status] || "Normal");

  // Plain ios-mode segment (native track + sliding indicator), compact and
  // content-sized so it shares one line with the row label.

  const searchQuery = tutorialSearch.toLowerCase().trim();
  const searchResults = searchQuery
    ? tutorialCategories.flatMap(c => (c.videos || []).map(v => ({ ...v, _catName: c.name }))).filter(v =>
        (v.title || "").toLowerCase().includes(searchQuery) ||
        (v.description || "").toLowerCase().includes(searchQuery)
      )
    : null;

  const showBack = tutorialVideo || selectedCategory;
  const modalTitle = tutorialVideo
    ? tutorialVideo.title
    : selectedCategory
      ? selectedCategory.name
      : t("Tutorials");

  return (
    <AppLayout>
      <IonPage>
        <IonContent>
          <div style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 16px 40px" }}>

            {/* Status */}
            <div style={{ marginBottom: 28 }}>
              <div style={cardStyle}>
                <div style={cardTitle}>{t("Status")}</div>

                <Row label={t("App Status")} right={<IonBadge color={statusColor} className="app-status-badge">{statusLabel}</IonBadge>} />
                <Row
                  label={`${t("Current version")}: ${currentV}`}
                  right={<span style={{ fontSize: "0.8rem", fontWeight: updateBehind ? 600 : 400, color: updateBehind ? "var(--ion-color-warning)" : "var(--ion-color-medium)" }}>{t("Latest")}: {latestV}</span>}
                />
                {updateBehind && storeUrl && (
                  <Row icon={arrowUpCircleOutline} label={t("Update MintSlip")} clickable right={<Chevron />}
                    onClick={() => window.open(storeUrl, "_blank", "noopener")} />
                )}

                {settings.videoUrl && (
                  <div style={{ padding: "16px 20px" }}>
                    <div style={{ maxWidth: 600, width: "100%" }}>
                      <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 8, overflow: "hidden" }}>
                        <iframe
                          src={youtubeEmbed(settings.videoUrl)}
                          title="What's New"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {settings.whatsNew && settings.whatsNew.length > 0 && (
                  <div style={{ paddingLeft: 20 }}>
                    <div style={{ paddingBottom: 14, paddingRight: 20, borderBottom: "1px solid var(--ion-border-color)" }}>
                      <div style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--ion-color-medium)", marginBottom: 8 }}>{t("What's New")}</div>
                      <ul style={{ margin: 0, padding: "0 0 0 20px", display: "flex", flexDirection: "column", gap: 5, listStyleType: "disc" }}>
                        {settings.whatsNew.map((item, i) => (
                          <li key={i} style={{ fontSize: "0.875rem", color: "var(--ion-text-color)", lineHeight: 1.6 }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                <div style={{ padding: "14px 20px" }}>
                  <div style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--ion-color-medium)", marginBottom: 8 }}>{t("Known Issues")}</div>
                  <ul style={{ margin: 0, padding: "0 0 0 16px", display: "flex", flexDirection: "column", gap: 5 }}>
                    {(settings.knownIssues?.length ? settings.knownIssues : ["None :)"]).map((item, i) => (
                      <li key={i} style={{ fontSize: "0.875rem", color: "var(--ion-color-medium)", lineHeight: 1.6 }}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Appearance */}
            <div style={{ marginBottom: 28 }}>
              <div style={cardStyle}>
                <div style={cardTitle}>{t("Appearance")}</div>
                <Row
                  icon={globeOutline}
                  label={t("Language")}
                  right={<LanguagePicker value={language} onChange={handleLanguage} />}
                />
                <Row
                  icon={moonOutline}
                  label={t("Dark theme")}
                  right={
                    <IonToggle
                      checked={darkMode}
                      onIonChange={handleDark}
                      style={{ "--handle-width": "20px", "--handle-height": "20px" }}
                    />
                  }
                />
                <Row
                  icon={helpCircleOutline}
                  label={t("Show support chat")}
                  last={!isNative}
                  right={
                    <IonToggle
                      checked={showChat}
                      onIonChange={handleChatToggle}
                      style={{ "--handle-width": "20px", "--handle-height": "20px" }}
                    />
                  }
                />
                {isNative && (
                  <Row
                    icon={notificationsOutline}
                    label={t("Allow push notifications")}
                    last
                    right={
                      <IonToggle
                        checked={allowPush}
                        onIonChange={handlePushToggle}
                        style={{ "--handle-width": "20px", "--handle-height": "20px" }}
                      />
                    }
                  />
                )}
              </div>
            </div>

            {/* Support */}
            <div style={{ marginBottom: 28 }}>
              <div style={cardStyle}>
                <div style={cardTitle}>{t("Support")}</div>
                <Row icon={logoYoutube} label={t("Tutorials")} clickable right={<Chevron />} onClick={() => setTutorialOpen(true)} />
                <Row icon={sendOutline}        label={t("Feature Request")} clickable right={<Chevron />} onClick={() => window.dispatchEvent(new CustomEvent("mintslip-open-support", { detail: { reason: "feature" } }))} />
                <Row icon={bugOutline}         label={t("Report a Problem")} clickable last right={<Chevron />} onClick={() => window.dispatchEvent(new CustomEvent("mintslip-open-support", { detail: { reason: "bug" } }))} />
              </div>
            </div>

            {/* About */}
            <div style={{ marginBottom: 28 }}>
              <div style={cardStyle}>
                <div style={cardTitle}>{t("About")}</div>
                <Row icon={documentOutline} label={t("Terms of Service")} clickable right={<Chevron />} onClick={() => navigate("/app/terms")} />
                <Row icon={documentLockOutline} label={t("Privacy Policy")}   clickable last right={<Chevron />} onClick={() => navigate("/app/privacy")} />
              </div>
            </div>

            <div style={{ textAlign: "center", color: "var(--ion-color-step-400)", fontSize: "0.78rem", marginTop: 8 }}>
              v{currentV}
            </div>

          </div>
        </IonContent>
      </IonPage>

      {/* ── Tutorials Modal — whodat's full-screen playlists modal: close /
          title / search in the header, a "Playlists" heading and a row of
          thumbnail cards (the admin's own image or the first video's YouTube
          thumbnail), the magnifier "No results found" state for searches ── */}
      <FreshOverlay as={IonModal} isOpen={tutorialOpen} onDidDismiss={closeTutorialModal} className="full-modal tutorials-modal">
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              {/* One button element for both back and close so its ripple
                  survives the view switch (swapping elements would drop it) */}
              <IonButton
                color="dark"
                shape="round"
                aria-label={showBack ? t("Back") : t("Close")}
                onClick={showBack ? handleTutorialBack : closeTutorialModalAfterRipple}
              >
                <IonIcon slot="icon-only" icon={showBack ? chevronBackOutline : closeOutline} />
              </IonButton>
            </IonButtons>
            {/* Middle: the title fades out while the search box scales in
                over it, exactly like the topbar search */}
            <div className="admin-topbar-mid">
              <div className={`admin-topbar-mid-content${tutorialSearchOpen && !tutorialVideo ? " is-hidden" : ""}`}>
                <IonTitle>{modalTitle}</IonTitle>
              </div>
              <SearchOverlay
                open={tutorialSearchOpen && !tutorialVideo}
                inputRef={tutorialSearchRef}
                value={tutorialSearch}
                onInput={(v) => { setTutorialSearch(v); setSelectedCategory(null); setTutorialVideo(null); }}
                onKeyDown={(e) => {
                  // Escape collapses the search only; stopped here so Ionic's
                  // document listener does not dismiss the whole modal
                  if (e.key === "Escape") { e.stopPropagation(); closeTutorialSearch(); }
                }}
                onCancel={closeTutorialSearch}
                placeholder={t("Search tutorials")}
                dark={darkMode}
              />
            </div>
            {!tutorialVideo && (
              <IonButtons slot="end">
                <SearchButton open={tutorialSearchOpen} onClick={() => setTutorialSearchOpen(true)} color="dark" />
              </IonButtons>
            )}
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {/* ── Video player ── */}
          {tutorialVideo && (
            <div style={{ padding: 16 }}>
              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, borderRadius: 10, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}>
                <iframe
                  src={youtubeEmbed(tutorialVideo.youtubeUrl)}
                  title={tutorialVideo.title}
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                />
              </div>
              {tutorialVideo.description && (
                <p style={{ marginTop: 14, fontSize: "0.9rem", color: "var(--ion-color-medium)", lineHeight: 1.6 }}>
                  {tutorialVideo.description}
                </p>
              )}
            </div>
          )}

          {/* ── Category video list ── */}
          {!tutorialVideo && selectedCategory && !searchQuery && (
            (selectedCategory.videos || []).length === 0 ? (
              <IonGrid><IonRow><NoResults text={t("No videos in this playlist yet")} /></IonRow></IonGrid>
            ) : (
              <div style={{ background: "var(--ion-card-background)" }}>
                {selectedCategory.videos.map((v, i) => (
                  <VideoListItem key={v.id || i} video={v} onClick={() => setTutorialVideo(v)} />
                ))}
              </div>
            )
          )}

          {/* ── Search results ── */}
          {!tutorialVideo && searchResults && (
            searchResults.length === 0 ? (
              <IonGrid><IonRow><NoResults /></IonRow></IonGrid>
            ) : (
              <div style={{ background: "var(--ion-card-background)" }}>
                {searchResults.map((v, i) => (
                  <VideoListItem key={v.id || i} video={v} onClick={() => setTutorialVideo(v)} />
                ))}
              </div>
            )
          )}

          {/* ── Playlists ── */}
          {!tutorialVideo && !selectedCategory && !searchResults && (
            <>
              <IonCardTitle className="playlist-title" style={{ paddingLeft: 16 }}>{t("Playlists")}</IonCardTitle>
              <IonGrid>
                <IonRow>
                  {tutorialCategories.map((cat, ci) => {
                    const thumb = categoryThumb(cat);
                    const n = (cat.videos || []).length;
                    return (
                      <IonCard key={cat.id || ci} button className="tutorial-card" onClick={() => setSelectedCategory(cat)}>
                        {thumb
                          ? <img src={thumb} alt={cat.name} />
                          : <div className="tutorial-card-placeholder"><IonIcon icon={playCircleOutline} /></div>}
                        <IonCardHeader className="border-top">
                          <IonCardTitle>{cat.name}</IonCardTitle>
                          <IonCardSubtitle>{n} {t(n !== 1 ? "videos" : "video")}</IonCardSubtitle>
                        </IonCardHeader>
                      </IonCard>
                    );
                  })}
                </IonRow>
                {tutorialCategories.length === 0 && (
                  <IonRow><NoResults text={t("No tutorials yet")} /></IonRow>
                )}
              </IonGrid>
            </>
          )}
        </IonContent>
      </FreshOverlay>
    </AppLayout>
  );
}
